import React from 'react';

import { Box } from '../Box';
import { Button } from '../Button';
import { Link } from '../Link';

export interface AccountFormControlsProps {
  /**
   * Name of the account form in analytics events, should be unique within the app
   * Convention: {appName}-{accountFormName}
   */
  analyticsName: string;
  /**
   * Text to display on the action button
   */
  actionText?: string;
  /**
   * Loading state for the action button
   */
  loading?: boolean;
  /**
   * Whether to show the forgot password link
   */
  showForgotPassword?: boolean;
  /**
   * Callback for requesting passwordless login
   */
  onRequestPasswordlessLogin?: () => void;
  /**
   * Loading state for passwordless login request
   */
  isRequestingPasswordlessLogin?: boolean;
  /**
   * Whether the submit button is disabled
   */
  disabled?: boolean;
  /**
   * Whether to open forgot password link in a new tab
   */
  openForgotPasswordInNewTab?: boolean;
  /**
   * Accessibility props for the submit button
   */
  submitA11yProps?: Pick<
    React.HTMLAttributes<HTMLButtonElement>,
    'aria-label' | 'aria-describedby' | 'aria-labelledby' | 'id' | 'tabIndex'
  >;
  /**
   * Text for the forgot password link
   * @default "Forgot password?"
   */
  forgotPasswordText?: string;
  /**
   * Aria label for forgot password link when opening in new window
   * @default "Forgot password? Opens in a new window"
   */
  forgotPasswordAriaNewWindow?: string;
  /**
   * Aria label for forgot password link when opening in same window
   * @default "Forgot password?"
   */
  forgotPasswordAriaSameWindow?: string;
}

/**
 * AccountFormControls provides the submit button and forgot password link
 * for account forms. This component is part of the account authentication flow.
 *
 * @example
 * ```tsx
 * <AccountFormControls
 *   analyticsName="my-app-login"
 *   actionText="Sign in"
 *   loading={isLoading}
 *   showForgotPassword={true}
 * />
 * ```
 */
export const AccountFormControls: React.FC<AccountFormControlsProps> = ({
  analyticsName,
  loading,
  onRequestPasswordlessLogin: _onRequestPasswordlessLogin,
  isRequestingPasswordlessLogin,
  disabled,
  actionText = 'Confirm',
  showForgotPassword = true,
  openForgotPasswordInNewTab,
  forgotPasswordText = 'Forgot password?',
  forgotPasswordAriaNewWindow = 'Forgot password? Opens in a new window',
  forgotPasswordAriaSameWindow = 'Forgot password?',
  submitA11yProps,
}) => {
  return (
    <Box gap="$20x">
      <Button
        testID="account-form-submit-button"
        variant="primary"
        type="submit"
        label={actionText}
        size="medium"
        analyticsName={`${analyticsName}-account-form-control-action-button`}
        width="100%"
        loading={loading && !isRequestingPasswordlessLogin}
        disabled={disabled}
        {...submitA11yProps}
      />
      {showForgotPassword && (
        <Link
          variant="primary"
          href="/reset-password"
          analyticsName={`${analyticsName}-forgot-password`}
          target={openForgotPasswordInNewTab ? '_blank' : '_self'}
          aria-label={
            openForgotPasswordInNewTab ? forgotPasswordAriaNewWindow : forgotPasswordAriaSameWindow
          }
          margin="auto"
        >
          {forgotPasswordText}
        </Link>
      )}
    </Box>
  );
};
